import React from "react";
import ServiceDetailLayout from "../../components/service-detail-layout";
import pressurePlate from "../../assets/img/pressure-plate-analysis.jpg";

/**
 * Pressure Plate Analysis detail page component
 */
const PressurePlateAnalysisDetail = () => {
  return (
    <ServiceDetailLayout
      title="Pressure Plate Analysis"
      description="Advanced pressure plate analysis producing 3D images of foot pressures when walking and standing. Professional gait assessment at The Moffat Foot Clinic."
      image={pressurePlate}
      imageAlt="Pressure Plate Analysis"
    >
      <h1 className="gs-u-mb+">Pressure Plate Analysis</h1>
      <p className="text--large gs-u-mb++">
        The method known as pressure plate analysis is essentially an
        examination carried out to evaluate the pressure exerted on the feet of
        an individual during various activities such as lifting objects or
        engaging in running and walking.
      </p>

      <p className="gs-u-mb++">
        To measure this force, a sophisticated device is employed. This device
        generates a visual representation that shows the distribution of
        pressure across different areas of the patient's foot. In summary, the
        main goal of a pressure plate analysis is to identify which area of the
        foot bears the most pressure.
      </p>

      <h2 className="gs-u-mb+">The Procedure</h2>
      <p className="gs-u-mb+">
        The process is simple and can be finished in just a few minutes. An
        orthoptist carries out the examination. To check a plate, the patient is
        requested to walk or run on a surface that acts like a mat, which is
        equipped with sensors. This mat is referred to as a pressure plate.
        Occasionally, for plate examinations, the patient may also be required
        to remain stationary.
      </p>
      <p className="gs-u-mb++">
        A computer is connected to the plate, and it presents data on how
        pressure is spread across your feet. As previously mentioned, the plate
        contains sensors that measure the pressure. Following a detailed review,
        the data is represented as a graph.
      </p>

      <h2 className="gs-u-mb+">Is there any purpose of the plate analysis?</h2>
      <p className="gs-u-mb++">
        Once you understand the concept of pressure analysis and the steps
        involved, you might question its importance. Yet, it's important to
        recognise that plate analysis offers numerous advantages. Therefore,
        it's wise to undergo the test. Not only is it straightforward, but the
        procedure is also quick and not overly complicated. Additionally, it
        delivers precise outcomes, revealing the distribution of pressure across
        your feet. Furthermore, the analysis allows for a comparison of pressure
        distribution between your feet.
      </p>

      <h2 className="gs-u-mb+">Advantages of Plate Analysis</h2>
      <p className="gs-u-mb+">
        As mentioned above, plate analysis has numerous benefits. These include
        the following:
      </p>
      <ul className="gs-u-mb++ benefits-list">
        <li>
          <strong>
            Assists in identifying illnesses because of an uneven distribution
            of pressure:
          </strong>{" "}
          Aches in the lower back and discomfort in the lower extremities happen
          because the pressure exerted on your feet is not evenly spread. This
          implies that through this examination, you can identify and address a
          range of ailments that arise due to an uneven distribution of
          pressure.
        </li>
        <li>
          <strong>Aids in locating remedies for peripheral neuropathy:</strong>{" "}
          The way you walk is significantly affected by the pressure on your
          feet. If you're unsure, gait pertains to the stance you adopt while
          moving, whether it's running, walking, or standing. This is because
          the support your feet offer during these activities affects your gait.
          Therefore, examining the plates can be beneficial in evaluating
          individuals diagnosed with peripheral neuropathy.
        </li>
        <li>
          <strong>Aids in managing additional illnesses:</strong> Some health
          issues, like issues within the musculoskeletal framework, frequently
          lead to a decrease in the strength of the muscles outside the main
          body parts. This leads to a lopsided application of the body's force.
          As a result, a person's walking pattern suffers, and discomfort in the
          lower parts of the body is often prevalent.
        </li>
        <li>
          <strong>Advantages for athletes and sportspeople:</strong> As
          previously stated, examining plates is extremely valuable for
          identifying and addressing a variety of issues related to imbalanced
          pressure and force distribution. Additionally, athletes can also gain
          from this examination. They can understand how the force is
          distributed across their foot and utilise this knowledge to improve
          their performance. Furthermore, they can prevent injuries to their
          muscles.
        </li>
      </ul>
    </ServiceDetailLayout>
  );
};

export default PressurePlateAnalysisDetail;
